import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Row,
  Col,
  Alert,
  Space,
  Skeleton,
  Tooltip,
} from 'antd';
import {
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  TeamOutlined,
  SendOutlined,
  ReloadOutlined,
  UserOutlined,
  GlobalOutlined,
  TagsOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../api/config/axiosConfig';
import { API_ENDPOINTS } from '../../api/endpoints/endpoints';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ORANGE = '#fe5a1d';
const ORANGE_LIGHT = 'rgba(254,90,29,0.08)';
const ORANGE_MEDIUM = 'rgba(254,90,29,0.15)';

interface NotificationStats {
  totalUsers: number;
  usersWithToken: number;
  iosUsers: number;
  androidUsers: number;
}

interface SendResult {
  sent: number;
  total: number;
  tickets: number;
}

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  iconBg: string;
  iconColor: string;
  loading: boolean;
  highlight?: boolean;
}> = ({ icon, label, value, iconBg, iconColor, loading, highlight }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 12,
      padding: '18px 20px',
      border: highlight ? `1.5px solid ${ORANGE}` : '1.5px solid #f0f0f0',
      boxShadow: highlight ? '0 4px 16px rgba(254,90,29,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
    }}
  >
    <div
      style={{
        width: 44, height: 44, borderRadius: 10, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, color: iconColor, flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      {loading ? (
        <Skeleton.Input size="small" active style={{ width: 60, height: 20 }} />
      ) : (
        <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? ORANGE : '#1a1a1a', lineHeight: 1.2 }}>
          {(value ?? 0).toLocaleString()}
        </div>
      )}
      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const TargetCard: React.FC<{
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}> = ({ selected, icon, label, description, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: selected ? ORANGE_LIGHT : '#fff',
      border: `1.5px solid ${selected ? ORANGE : '#e8e8e8'}`,
      borderRadius: 10,
      padding: '12px 14px',
      cursor: 'pointer',
      transition: 'all 0.18s ease',
      position: 'relative',
      userSelect: 'none',
    }}
  >
    {selected && (
      <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: ORANGE, fontSize: 14 }} />
    )}
    <div style={{ fontSize: 20, marginBottom: 6, color: selected ? ORANGE : '#8c8c8c' }}>{icon}</div>
    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.4 }}>{description}</div>
  </div>
);

const PushNotifications: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [target, setTarget] = useState<'all' | 'role' | 'user'>('all');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.NOTIFICATION_STATS);
      if (res.data?.success) setStats(res.data.data);
    } catch {
      // optional
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    void fetchStats();
  }, []);

  const onFinish = async (values: {
    title: string;
    message: string;
    type: string;
    role?: string;
    userId?: string;
  }) => {
    setSending(true);
    setError(null);
    setLastResult(null);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.SEND_NOTIFICATION, {
        title: values.title,
        message: values.message,
        type: values.type,
        target,
        role: values.role,
        userId: values.userId,
      });
      if (res.data?.success) {
        setLastResult(res.data.data as SendResult);
        form.resetFields(['title', 'message', 'role', 'userId']);
        void fetchStats();
      }
    } catch (err: any) {
      setError(err?.message || t('admin.push_notifications.send_failed'));
    } finally {
      setSending(false);
    }
  };

  const reachCount = target === 'all' ? stats?.usersWithToken : undefined;
  const coveragePercent =
    stats && stats.totalUsers > 0
      ? Math.round((stats.usersWithToken / stats.totalUsers) * 100)
      : 0;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.push_notifications.title')}</Title>
          <Text type="secondary">{t('admin.push_notifications.subtitle')}</Text>
        </div>
        <Tooltip title={t('admin.push_notifications.refresh_tooltip')}>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchStats}
            loading={statsLoading}
            className="admin-filter-btn"
          >
            {t('admin.push_notifications.btn_refresh_stats')}
          </Button>
        </Tooltip>
      </div>

      {/* Stats row */}
      <Row gutter={16}>
        <Col xs={12} md={6}>
          <StatCard
            icon={<TeamOutlined />}
            label={t('admin.push_notifications.stat_total_users')}
            value={stats?.totalUsers}
            iconBg="rgba(22,119,255,0.1)"
            iconColor="#1677ff"
            loading={statsLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<MobileOutlined />}
            label={t('admin.push_notifications.stat_registered_devices')}
            value={stats?.usersWithToken}
            iconBg={ORANGE_MEDIUM}
            iconColor={ORANGE}
            loading={statsLoading}
            highlight
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<AppleOutlined />}
            label={t('admin.push_notifications.stat_ios')}
            value={stats?.iosUsers}
            iconBg="rgba(0,0,0,0.06)"
            iconColor="#1a1a1a"
            loading={statsLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            icon={<AndroidOutlined />}
            label={t('admin.push_notifications.stat_android')}
            value={stats?.androidUsers}
            iconBg="rgba(61,220,132,0.12)"
            iconColor="#2da44e"
            loading={statsLoading}
          />
        </Col>
      </Row>

      {/* Coverage bar */}
      {stats && (
        <div
          style={{
            background: '#fff', borderRadius: 12, padding: '14px 20px',
            border: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16,
          }}
        >
          <Text style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
            {t('admin.push_notifications.coverage_label')}
          </Text>
          <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                width: `${coveragePercent}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${ORANGE}, #ff7b4b)`,
                borderRadius: 3,
                transition: 'width 0.6s ease',
              }}
            />
          </div>
          <Text style={{ fontSize: 13, fontWeight: 700, color: ORANGE, whiteSpace: 'nowrap' }}>
            {coveragePercent}%
          </Text>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {t('admin.push_notifications.coverage_users', { used: stats.usersWithToken, total: stats.totalUsers })}
          </Text>
        </div>
      )}

      {/* Feedback banners */}
      {lastResult && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setLastResult(null)}
          message={
            <span>
              <strong>{t('admin.push_notifications.notif_sent')}</strong> {t('admin.push_notifications.delivered_to')}{' '}
              <strong>{lastResult.sent}</strong>{' '}
              {lastResult.sent !== 1 ? t('admin.push_notifications.devices_plural') : t('admin.push_notifications.devices')}{' '}
              {t('admin.push_notifications.across')}{' '}
              <strong>{lastResult.total}</strong>{' '}
              {lastResult.total !== 1 ? t('admin.push_notifications.matched_users_plural') : t('admin.push_notifications.matched_users')}.
            </span>
          }
          style={{ borderRadius: 10 }}
        />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          message={error}
          style={{ borderRadius: 10 }}
        />
      )}

      {/* Main two-column layout */}
      <Row gutter={24} align="top">
        {/* Left: compose form */}
        <Col xs={24} lg={16}>
          <div
            style={{
              background: '#fff', borderRadius: 14, border: '1.5px solid #f0f0f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <div
              style={{
                padding: '18px 24px', borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <div
                style={{
                  width: 32, height: 32, borderRadius: 8, background: ORANGE_MEDIUM,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <SendOutlined style={{ color: ORANGE, fontSize: 15 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>
                  {t('admin.push_notifications.compose_title')}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('admin.push_notifications.fill_details')}</div>
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              <Form form={form} layout="vertical" initialValues={{ type: 'system' }} onFinish={onFinish}>
                {/* Title + type */}
                <Row gutter={12}>
                  <Col xs={24} sm={16}>
                    <Form.Item
                      name="title"
                      label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('admin.push_notifications.label_title')}</span>}
                      rules={[{ required: true, message: t('admin.push_notifications.validation_required') }]}
                      style={{ marginBottom: 16 }}
                    >
                      <Input
                        placeholder={t('admin.push_notifications.placeholder_title')}
                        maxLength={80}
                        showCount
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="type"
                      label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('admin.push_notifications.label_type')}</span>}
                      style={{ marginBottom: 16 }}
                    >
                      <Select style={{ borderRadius: 8 }}>
                        <Select.Option value="system">{t('admin.push_notifications.type_system')}</Select.Option>
                        <Select.Option value="listing">{t('admin.push_notifications.type_listing')}</Select.Option>
                        <Select.Option value="other">{t('admin.push_notifications.type_other')}</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="message"
                  label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('admin.push_notifications.label_message')}</span>}
                  rules={[{ required: true, message: t('admin.push_notifications.validation_required') }]}
                  style={{ marginBottom: 24 }}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('admin.push_notifications.placeholder_message')}
                    maxLength={300}
                    showCount
                    style={{ borderRadius: 8, resize: 'none', paddingBottom: 24 }}
                  />
                </Form.Item>

                {/* Target audience */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', marginBottom: 10 }}>
                    {t('admin.push_notifications.label_target')}
                  </div>
                  <Row gutter={10}>
                    <Col span={8}>
                      <TargetCard
                        selected={target === 'all'}
                        icon={<GlobalOutlined />}
                        label={t('admin.push_notifications.target_all')}
                        description={t('admin.push_notifications.target_all_desc')}
                        onClick={() => setTarget('all')}
                      />
                    </Col>
                    <Col span={8}>
                      <TargetCard
                        selected={target === 'role'}
                        icon={<TagsOutlined />}
                        label={t('admin.push_notifications.target_role')}
                        description={t('admin.push_notifications.target_role_desc')}
                        onClick={() => setTarget('role')}
                      />
                    </Col>
                    <Col span={8}>
                      <TargetCard
                        selected={target === 'user'}
                        icon={<UserOutlined />}
                        label={t('admin.push_notifications.target_user')}
                        description={t('admin.push_notifications.target_user_desc')}
                        onClick={() => setTarget('user')}
                      />
                    </Col>
                  </Row>
                </div>

                {target === 'role' && (
                  <Form.Item
                    name="role"
                    label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('admin.users.label_role')}</span>}
                    rules={[{ required: true, message: t('admin.push_notifications.select_role_validation') }]}
                    style={{ marginBottom: 16 }}
                  >
                    <Select placeholder={t('admin.push_notifications.select_role')} style={{ maxWidth: 280, borderRadius: 8 }}>
                      <Select.Option value="PRIVATE">
                        <Space><UserOutlined /> {t('admin.push_notifications.role_private')}</Space>
                      </Select.Option>
                      <Select.Option value="DEALER">
                        <Space><TagsOutlined /> {t('admin.push_notifications.role_dealer')}</Space>
                      </Select.Option>
                      <Select.Option value="ADMIN">
                        <Space><TeamOutlined /> {t('admin.push_notifications.role_admin')}</Space>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                )}

                {target === 'user' && (
                  <Form.Item
                    name="userId"
                    label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('admin.push_notifications.label_user_id')}</span>}
                    rules={[{ required: true, message: t('admin.push_notifications.user_id_validation') }]}
                    style={{ marginBottom: 16 }}
                  >
                    <Input
                      placeholder={t('admin.push_notifications.placeholder_user_id')}
                      prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                      style={{ borderRadius: 8, maxWidth: 420 }}
                    />
                  </Form.Item>
                )}

                {target === 'all' && stats && (
                  <div
                    style={{
                      background: ORANGE_LIGHT,
                      border: `1px solid ${ORANGE}30`,
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginBottom: 20,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <MobileOutlined style={{ color: ORANGE }} />
                    <Text style={{ fontSize: 13, color: '#1a1a1a' }}>
                      {t('admin.push_notifications.estimated_reach')}{' '}
                      <strong style={{ color: ORANGE }}>
                        {(reachCount ?? 0).toLocaleString()}{' '}
                        {reachCount !== 1 ? t('admin.push_notifications.devices_unit') : t('admin.push_notifications.device_unit')}
                      </strong>
                    </Text>
                  </div>
                )}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={sending}
                    icon={<SendOutlined />}
                    size="large"
                    style={{
                      background: ORANGE,
                      borderColor: ORANGE,
                      borderRadius: 9,
                      fontWeight: 600,
                      height: 44,
                      paddingInline: 28,
                      boxShadow: '0 4px 14px rgba(254,90,29,0.35)',
                    }}
                  >
                    {sending ? t('admin.push_notifications.sending') : t('admin.push_notifications.btn_send')}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </Col>

        {/* Right: platform breakdown + tips */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {/* Platform breakdown */}
            <div
              style={{
                background: '#fff',
                borderRadius: 14,
                border: '1.5px solid #f0f0f0',
                padding: '20px 24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', marginBottom: 16 }}>
                {t('admin.push_notifications.platform_breakdown')}
              </div>
              {statsLoading ? (
                <Skeleton active paragraph={{ rows: 2 }} />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={14}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <Space size={6}>
                        <AppleOutlined style={{ fontSize: 13 }} />
                        <Text style={{ fontSize: 13 }}>iOS</Text>
                      </Space>
                      <Text style={{ fontSize: 13, fontWeight: 600 }}>{stats?.iosUsers ?? 0}</Text>
                    </div>
                    <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: stats && stats.usersWithToken > 0 ? `${(stats.iosUsers / stats.usersWithToken) * 100}%` : '0%',
                          height: '100%',
                          background: '#1a1a1a',
                          borderRadius: 3,
                          transition: 'width 0.5s ease',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <Space size={6}>
                        <AndroidOutlined style={{ fontSize: 13, color: '#2da44e' }} />
                        <Text style={{ fontSize: 13 }}>Android</Text>
                      </Space>
                      <Text style={{ fontSize: 13, fontWeight: 600 }}>{stats?.androidUsers ?? 0}</Text>
                    </div>
                    <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: stats && stats.usersWithToken > 0 ? `${(stats.androidUsers / stats.usersWithToken) * 100}%` : '0%',
                          height: '100%',
                          background: '#2da44e',
                          borderRadius: 3,
                          transition: 'width 0.5s ease',
                        }}
                      />
                    </div>
                  </div>
                </Space>
              )}
            </div>

            {/* Tips */}
            <div
              style={{
                background: ORANGE_LIGHT,
                borderRadius: 14,
                border: `1px solid ${ORANGE}25`,
                padding: '16px 20px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13, color: ORANGE, marginBottom: 10 }}>
                {t('admin.push_notifications.tips_title')}
              </div>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {[
                  t('admin.push_notifications.tip_titles'),
                  t('admin.push_notifications.tip_messages'),
                  t('admin.push_notifications.tip_cta'),
                  t('admin.push_notifications.tip_frequency'),
                ].map((tip) => (
                  <div key={tip} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: ORANGE,
                        marginTop: 6,
                        flexShrink: 0,
                      }}
                    />
                    <Text style={{ fontSize: 12, color: '#5a5a5a', lineHeight: 1.5 }}>{tip}</Text>
                  </div>
                ))}
              </Space>
            </div>
          </Space>
        </Col>
      </Row>
    </Space>
  );
};

export default PushNotifications;
